/**
 * External dependencies
 */
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import Breadcrumbs from '@Components/Breadcrumbs';
import VenueQuickFilter from '@Components/CustomDataTableFilters/VenueQuickFilter';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { EventBookingEvent } from '@Types/eventBooking';
import {
  buildFilterParams,
  formatPhoneNumberFriendlyDisplay,
} from '@Utils/utils';
import { CalendarDays } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useEventBookingEvents } from '../../hooks/useEventBookingEvents';

const columnConfigs: ColumnConfig<EventBookingEvent>[] = [
  {
    id: 'name',
    headerLabel: 'Event Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'spaces',
    headerLabel: 'Space',
    accessor: (row) => row.spaces?.map((s) => s.name).join(', ') || '-',
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'venueId',
    headerLabel: 'Venue',
    accessor: (row) => row.venue?.name ?? '-',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Link to={`/venues/${row.venue?.id}`} className="hover:underline">
            {row.venue?.name ?? '-'}
          </Link>
        </div>
      );
    },
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Booking Date',
    accessor: (row) =>
      row.createdAt
        ? new Date(row.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : '-',
    type: 'text',
  },
  {
    id: 'startDate',
    headerLabel: 'Event Date',
    accessor: (row) =>
      row.startDate
        ? new Date(row.startDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '-',
    type: 'text',
  },
  {
    id: 'guestCount',
    headerLabel: 'Guests',
    accessor: (row) => String(row.guestCount ?? 0),
    type: 'text',
  },
  {
    id: 'eventTypeId',
    headerLabel: 'Event Type',
    accessor: (row) => row.eventType?.name ?? '-',
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'fishProfile.fName',
    headerLabel: 'First Name',
    accessor: (row) => row.fishProfile.fName ?? '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'fishProfile.lName',
    headerLabel: 'Last Name',
    accessor: (row) => row.fishProfile.lName ?? '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'fishProfile.email',
    headerLabel: 'Email',
    accessor: (row) => row.fishProfile.email ?? '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'fish.phoneNumber',
    headerLabel: 'Phone',
    accessor: (row) =>
      row.fish.phoneNumber
        ? formatPhoneNumberFriendlyDisplay(row.fish.phoneNumber)
        : '-',
    type: 'text',
    enableSorting: false,
  },
];

export default function PrivateDiningEvents() {
  const navigate = useNavigate();

  const controller = useResourceTableController<EventBookingEvent>({
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    columnsConfig: columnConfigs,
    baseSearchParams: () => ({
      include: 'venue,eventType,spaces,fish,fishProfile',
    }),
    buildFilterParams: (filters) => buildFilterParams(filters),
    quickFilterFields: [{ field: 'name', label: 'Event Name' }],
    initialPageSize: 10,
  });

  const { data: payload, status } = useEventBookingEvents(
    controller.searchParams,
  );

  const rows: EventBookingEvent[] = payload?.data ?? [];
  const meta = payload?.meta;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Private Dinining', href: '/private-dining-events' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center">
        <div>
          <Heading>Private Dining Events</Heading>
          <p>Manage your private dining events.</p>
        </div>
      </div>
      <Card>
        <CardContent>
          <ResourceTableView<EventBookingEvent>
            label="Events"
            controller={controller}
            data={rows}
            meta={meta}
            status={status}
            summaryIcon={<CalendarDays className="h-4 w-4 text-zinc-500" />}
            summaryLabel="Total Events: "
            onRowClick={(row) => navigate(`/private-dining-events/${row.id}`)}
            renderQuickFiltersExtra={({ table }) => (
              <VenueQuickFilter<EventBookingEvent> table={table} />
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
